import { BlockedTime } from 'src/common/providers/useBookingMotivators';
import { TripCartPrice } from 'src/common/types';
import { InstabookAvailability } from 'src/common/types/api/v4/instabook';

// ======================================================================
// Instabooks
// ======================================================================
type InstabookCacheValue = {
  availabilities: InstabookAvailability[];
  durations: number[];
};

const instabookCache: Map<string, InstabookCacheValue> = new Map();

// search params
type InstabookCacheKey = {
  startDate: string;
  capacity: string;
  currency: string;
};

// Compression isn't super important, as long as it's unique
const encodeInstabookKey = ({
  startDate, capacity, currency,
}: InstabookCacheKey) => `d:${startDate},g:${capacity},c:${currency}`;

export const getCachedInstabookOnDate = (key: InstabookCacheKey): InstabookCacheValue | null => {
  const cacheKey = encodeInstabookKey(key);
  if (instabookCache.has(cacheKey)) {
    return instabookCache.get(cacheKey)!;
  }

  return null;
};

export const updateCachedInstabooksOnDate = (
  key: InstabookCacheKey,
  value: InstabookCacheValue,
): void => {
  const cacheKey = encodeInstabookKey(key);
  instabookCache.set(cacheKey, value);
};

// ======================================================================
// Tiers
// ======================================================================

// search params
type TierCacheKey = {
  captainOption: string;
  guests: string;
  currency: string;
  duration: string;
};

export type TierCacheValue = TripCartPrice;

const tierCache: Map<string, TierCacheValue> = new Map();

// Compression isn't super important, as long as it's unique
const encodeTierKey = ({
  captainOption, guests, currency, duration,
}: TierCacheKey) => `d:${duration},g:${guests},c:${currency},o:${captainOption}`;

export const getCachedTierPrices = (key: TierCacheKey): TierCacheValue | null => {
  const cacheKey = encodeTierKey(key);
  if (tierCache.has(cacheKey)) {
    return tierCache.get(cacheKey)!;
  }

  return null;
};

export const setCachedTierPrices = (key: TierCacheKey, value: TierCacheValue): void => {
  const cacheKey = encodeTierKey(key);
  tierCache.set(cacheKey, value);
};

// ======================================================================
// Blocked Time
// ======================================================================

type BlockedTimeCacheKey = {
  date: string;
};
type BlockedTimeCacheValue = BlockedTime[];

const blockedTimeCache: Map<string, BlockedTimeCacheValue> = new Map();

const encodeBlockedTimeKey = ({
  date,
}: BlockedTimeCacheKey) => `d:${date}`;

export const getCachedBlockedTimes = (key: BlockedTimeCacheKey): BlockedTimeCacheValue | null => {
  const cacheKey = encodeBlockedTimeKey(key);
  if (blockedTimeCache.has(cacheKey)) {
    return blockedTimeCache.get(cacheKey)!;
  }

  return null;
};

export const setCachedBlockedTimes = (
  key: BlockedTimeCacheKey,
  value: BlockedTimeCacheValue,
): void => {
  const cacheKey = encodeBlockedTimeKey(key);
  blockedTimeCache.set(cacheKey, value);
};

// ======================================================================
// Clear
// ======================================================================

export const clearCaches = (): void => {
  tierCache.clear();
  instabookCache.clear();
  blockedTimeCache.clear();
};
